/**
 * File: useTourOverlayController.ts
 * Path: apps/admin/src/modules/layout/hooks/useTourOverlayController.ts
 * Description: Runs a tutorial. Finds and measures the step's target (the page may still be loading), opens a
 * step's own page first, ends a page tour when you leave its page, closes on Escape, and works out where the
 * lit hole and the small box go.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useHelp, useTour } from "@/shared/hooks/useTourState";
import { holeFor, rectOf, tipStyleFor, type Box } from "@/modules/layout/utils/tourLayoutUtils";

const FIND_FOR_MS = 3000;

export const useTourOverlayController = () => {
  const { steps, title, index, running, next, back, stop } = useTour();
  const openHelp = useHelp((s) => s.openHelp);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const step = running ? steps[index] : undefined;
  // Where the target was last measured, and for which step: a measurement of an earlier step is ignored.
  const [located, setLocated] = useState<{ key: string; target: Box | null; frame: Box | null } | null>(null);
  const key = `${index}|${step?.target ?? ""}|${pathname}`;
  const target = located?.key === key ? located.target : null;
  const frame = located?.key === key ? located.frame : null;
  const nextRef = useRef<HTMLButtonElement>(null);

  // A page tour belongs to its page: leaving the page ends it.
  const startedOn = useRef(pathname);
  useEffect(() => {
    if (!running) startedOn.current = pathname;
    else if (!steps.some((s) => s.path) && pathname !== startedOn.current) stop();
  }, [running, steps, pathname, stop]);

  // A step on another page opens it first.
  useEffect(() => {
    if (step?.path && step.path !== pathname) navigate(step.path);
  }, [step, pathname, navigate]);

  // Find the target (the page may still be loading), then keep it located.
  useLayoutEffect(() => {
    if (!step || (step.path && step.path !== pathname)) return;
    let live = true;
    let el: Element | null = null;
    const started = Date.now();
    const measure = () => {
      const main = document.querySelector("main");
      setLocated({ key, frame: main ? rectOf(main) : null, target: el ? rectOf(el) : null });
    };
    const look = window.setInterval(() => {
      el = document.querySelector(step.target);
      if (el) {
        window.clearInterval(look);
        el.scrollIntoView({ block: "center", behavior: "auto" });
        if (live) measure();
      } else if (Date.now() - started > FIND_FOR_MS) {
        window.clearInterval(look);
        if (live) measure();
      }
    }, 100);
    const onChange = () => live && measure();
    window.addEventListener("resize", onChange);
    document.addEventListener("scroll", onChange, true);
    return () => {
      live = false;
      window.clearInterval(look);
      window.removeEventListener("resize", onChange);
      document.removeEventListener("scroll", onChange, true);
    };
  }, [step, pathname, key]);

  useEffect(() => {
    if (!running) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") stop();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [running, stop]);

  useEffect(() => {
    if (step) nextRef.current?.focus();
  }, [step, target]);

  return {
    step,
    index,
    title,
    total: steps.length,
    last: index + 1 === steps.length,
    frame,
    hole: holeFor(target, frame),
    tipStyle: tipStyleFor(target, { width: window.innerWidth, height: window.innerHeight }),
    nextRef,
    next,
    back,
    stop,
    backToList: () => {
      stop();
      openHelp();
    },
  };
};
